/**
 * TUI session selector for --resume flag
 */

import { ProcessTerminal, setCapabilityOverrides, setKeybindings, TuiMainScreen } from "@earendil-works/pi-tui";
import { getAgentDir } from "../config.js";
import { KeybindingsManager } from "../core/keybindings.ts";
import type { SessionInfo, SessionListProgress } from "../core/session-manager.ts";
import type { SettingsManager } from "../core/settings-manager.ts";
import { SessionSelectorComponent } from "../modes/interactive/components/session-selector.ts";

type SessionsLoader = (onProgress?: SessionListProgress) => Promise<SessionInfo[]>;

/** Show TUI session selector and return the selected row with its store scope, or null if cancelled. */
export async function selectSession(
	currentSessionsLoader: SessionsLoader,
	allSessionsLoader: SessionsLoader,
	settingsManager: SettingsManager,
	deleteSession?: (sessionId: string) => Promise<void>,
): Promise<SessionInfo | null> {
	setCapabilityOverrides(settingsManager.getTerminalCapabilityOverrides());
	return new Promise((resolve) => {
		const ui = new TuiMainScreen(new ProcessTerminal(), undefined, getAgentDir());
		const keybindings = KeybindingsManager.create();
		setKeybindings(keybindings);
		let resolved = false;

		const selector = new SessionSelectorComponent(
			currentSessionsLoader,
			allSessionsLoader,
			(session) => {
				if (!resolved) {
					resolved = true;
					selector.dispose();
					ui.stop();
					resolve(session);
				}
			},
			() => {
				if (!resolved) {
					resolved = true;
					selector.dispose();
					ui.stop();
					resolve(null);
				}
			},
			() => {
				selector.dispose();
				ui.stop();
				process.exit(0);
			},
			() => ui.requestRender(),
			{ showRenameHint: false, keybindings, deleteSession },
		);

		ui.addChild(selector);
		ui.setFocus(selector.getSessionList());
		ui.start();
	});
}
